import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Delete as DeleteIcon, Edit as EditIcon } from '@material-ui/icons'
import { FlexHorizontal, Spacer, useToasts } from '@vestaboard/installables'
import React, { useState } from 'react'

import { Alert } from '@material-ui/lab'
import { AlertDialog } from '../../alert-dialog'
import { GridCheckIcon } from '@material-ui/data-grid'
import { Link } from '../../link'
import { useDeleteHomeScreenBanner } from '../hooks/useDeleteHomescreenBanner'
import { useListHomeScreenBanners } from '../hooks/useListHomescreenBanner'

interface IBannerList {
  onEdit: (bannerId: string) => void
  onCreate: () => void
}

export const BannerList = (props: IBannerList) => {
  const { data, loading, error, refetch } = useListHomeScreenBanners()
  const [deleteHomeScreenBanner] = useDeleteHomeScreenBanner()
  const { addToast } = useToasts()
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    bannerId?: string
    bannerTitle?: string
  }>({ open: false })

  const handleDeleteClick = (bannerId: string, bannerTitle: string) => {
    setDeleteDialog({
      open: true,
      bannerId,
      bannerTitle
    })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.bannerId) return

    try {
      const result = await deleteHomeScreenBanner({
        variables: { id: deleteDialog.bannerId }
      })

      const deleteResult = result.data?.deleteHomeScreenBanner

      if (deleteResult?.__typename === 'HomeScreenBannerV2') {
        addToast('Banner deleted successfully', {
          autoDismiss: true,
          appearance: 'success'
        })
        refetch()
      } else {
        addToast(`Error: ${deleteResult?.error || 'Failed to delete banner'}`, {
          autoDismiss: true,
          appearance: 'error'
        })
      }
    } catch (err) {
      addToast('Failed to delete banner', {
        autoDismiss: true,
        appearance: 'error'
      })
    }

    setDeleteDialog({ open: false })
  }

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false })
  }

  return (
    <Box>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Home Screen Banners</Typography>
        <Button onClick={props.onCreate} variant='contained' color='primary'>
          Create New Banner
        </Button>
      </FlexHorizontal>
      <Spacer size='extraLarge' />

      {loading ? (
        <CircularProgress />
      ) : error || !data ? (
        <Alert severity='error'>There was an error loading the banners.</Alert>
      ) : data.homeScreenBanners?.length ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Position</TableCell>
                <TableCell style={{ width: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.homeScreenBanners.map(banner => (
                <TableRow key={banner.id}>
                  <TableCell>
                    <Link to={`/home-screen-banner/${banner.id}`}>{banner.title || 'No Title'}</Link>
                  </TableCell>
                  <TableCell>{banner.description || 'No Description'}</TableCell>
                  <TableCell>{banner.enabled ? <GridCheckIcon /> : null}</TableCell>
                  <TableCell>{banner.positionInFeed}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => props.onEdit(banner.id)} size='small' title='Edit Banner'>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteClick(banner.id, banner.title || 'Untitled Banner')}
                      size='small'
                      title='Delete Banner'
                      color='secondary'>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity='info'>No banners have been created yet.</Alert>
      )}

      <AlertDialog
        open={deleteDialog.open}
        title='Delete Banner'
        description={`Are you sure you want to delete "${deleteDialog.bannerTitle}"? This action cannot be undone.`}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  )
}
